import { ArrowPathIcon } from '@heroicons/react/24/outline';
import React from 'react';

import { DiscriminatedTreatmentRequestStatusChangedEvent } from '@/services/api/generated/matterChangesService';
import { useGetMedicalProvidersQuery } from '@/services/api/treatmentService';

import {
  ActivityActions,
  ActivityBody,
  ActivityIcon,
  ActivityItem,
  ActivityLink,
  ActivityStatusTransition,
  CollapsibleList,
} from '../activity-feed-v2';
import { getUserFromEvent } from '../activity-feed-v2/ActivityDiffList';
import { formatEnumValue, formatStatus } from '../activity-feed-v2/utils';

export interface TreatmentRequestStatusChangedEventProps {
  event: DiscriminatedTreatmentRequestStatusChangedEvent;
}

interface TreatmentRequestDetailField {
  label: string;
  value: string | number | boolean | null | undefined;
}

export const TreatmentRequestStatusChangedEvent: React.FC<
  TreatmentRequestStatusChangedEventProps
> = ({ event }) => {
  const { treatment_request, old_status, new_status, reason } = event.payload;
  const user = getUserFromEvent(event);
  const { data: providers } = useGetMedicalProvidersQuery(event.matter_id);

  const provider = providers?.find(
    (p) => p.id === treatment_request.provider_id,
  );

  const icon = (
    <ActivityIcon
      icon={
        <div className="flex h-6 w-6 items-center justify-center rounded-full bg-indigo-100">
          <ArrowPathIcon className="h-4 w-4 text-indigo-600" />
        </div>
      }
    />
  );

  const treatmentRequestDetailFields: TreatmentRequestDetailField[] = [
    { label: 'Provider', value: provider?.name },
    { label: 'Reason', value: reason ? formatEnumValue(reason) : null },
  ];

  const detailItems = treatmentRequestDetailFields
    .filter(
      ({ value }) => value !== null && value !== undefined && value !== '',
    )
    .map(({ label, value }) => `${label}: ${value}`);

  const body = (
    <ActivityBody>
      {old_status && new_status && (
        <ActivityStatusTransition
          from={formatStatus(old_status)}
          to={formatStatus(new_status)}
        />
      )}
      {detailItems.length > 0 && (
        <CollapsibleList
          items={detailItems}
          showLabel="Show Details"
          hideLabel="Hide Details"
        />
      )}
    </ActivityBody>
  );

  const actions = event.payload.treatment_request?.provider_id && (
    <ActivityActions>
      <ActivityLink
        href={`/matters/${event.matter_id}/treatment/${event.payload.treatment_request.provider_id}`}
      >
        View Provider
      </ActivityLink>
    </ActivityActions>
  );

  return (
    <ActivityItem
      icon={icon}
      title="Treatment Request Status Changed"
      timestamp={new Date(event.occurred_at ?? new Date())}
      user={user}
      body={body}
      actions={actions}
    />
  );
};
